import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTrashAlt } from "react-icons/fa";

const DeleteConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  invoiceNo,
  loading = false,
}) => {
  const [reason, setReason] = useState("");
  const confirmBtnRef = useRef(null);

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape" && !loading) {
        e.preventDefault();
        onCancel?.();
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onCancel]);

  const handleConfirm = () => {
    if (!reason.trim()) return;
    onConfirm?.(reason.trim());
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => (!loading ? onCancel?.() : null)}
          />

          {/* Dialog */}
          <motion.div
            className="relative w-full max-w-md mx-3"
            initial={{ scale: 0.95, y: -20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: -20 }}
          >
            <div className="bg-white rounded-xl shadow-2xl border">
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full grid place-items-center bg-rose-100 text-rose-700">
                    <FaTrashAlt />
                  </div>

                  <div>
                    <h3 className="text-base font-semibold text-slate-800">
                      Delete invoice?
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Invoice {invoiceNo || "—"} will be permanently deleted.
                    </p>
                  </div>
                </div>

                {/* Reason Input */}
                <div className="mt-4">
                  <label className="text-xs text-slate-500 font-medium">
                    Delete Reason *
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full mt-1 px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-rose-400"
                    placeholder="Enter reason for deleting..."
                  />
                </div>

                <div className="mt-5 flex gap-2 justify-end">
                  <button
                    disabled={loading}
                    onClick={onCancel}
                    className="px-4 py-2 rounded-md border bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-60"
                  >
                    Cancel
                  </button>

                  <button
                    ref={confirmBtnRef}
                    disabled={loading || !reason.trim()}
                    onClick={handleConfirm}
                    className="px-4 py-2 rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                  >
                    {loading ? "Please wait…" : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeleteConfirmDialog;
